/**
 * Regras de seleção de tipos por canal na tela de preferências de notificação.
 *
 * **REGRA ÚNICA** (2026-08-17), a mesma dos dois lados: só é entregue o que
 * está MARCADO na lista daquele canal.
 *
 *   - `[...]`     → só os listados
 *   - `[]`        → nenhum
 *   - `undefined` → nenhum (lista nunca gravada — legado pré-migração)
 *
 * O que sumiu: a semântica dupla por canal (`isOptInNotificationChannel` no
 * render/toggle) e o colapso "marquei tudo → volta pra undefined". Os dois
 * existiam porque `undefined` significava "recebe tudo" em sino/push/som; hoje
 * significa o oposto, então colapsar para `undefined` silenciaria o canal
 * inteiro — o checkbox mentiria de novo, agora ao contrário.
 *
 * Espelha `isTypeAllowedForChannel` (`notifications/service.ts`) no backend.
 * Incidente 2026-08-11 (UI dizia marcado, dispatch não entregava) e incidente
 * 2026-08-17 (avisos de SLA para quem nunca configurou) nasceram os dois de
 * regra duplicada — não reintroduzir exceção por canal aqui.
 */
import type { NotificationType } from '@troia-v3/types';

/** O checkbox do par (canal, tipo) aparece marcado? */
export function isTypeCheckedForChannel(
  types: string[] | undefined,
  type: NotificationType,
): boolean {
  return types?.includes(type) ?? false;
}

/**
 * Nova lista de tipos após clicar no checkbox do par (canal, tipo).
 * Sempre retorna lista EXPLÍCITA — nunca `undefined`, que significaria
 * "silêncio total" no canal.
 */
export function toggleTypeForChannel(
  types: string[] | undefined,
  type: NotificationType,
): NotificationType[] {
  const atual = (types ?? []) as NotificationType[];

  return atual.includes(type)
    ? atual.filter(t => t !== type)
    : [...atual, type];
}
